import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import GrantList from './GrantList';
import BankStatement from './BankStatement';
import ConfirmedTransactions from './ConfirmedTransactions';
import FinancialsImport from './FinancialsImport';
import PMSPayments from './PMSPayments';

const GrantFundFinancials = ({ userInfo }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Determine if user is admin
  const isAdmin = userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin';

  // Map URL tab param to internal tab names
  const getTabFromParam = (param) => {
    switch (param) {
      case 'unconfirmed':
        return 'unconfirmed-transactions';
      case 'confirmed':
        return 'confirmed-transactions';
      case 'grants':
      case 'grant-reports':
        return 'grant-reports';
      case 'import':
      case 'financials-import':
        return 'financials-import';
      case 'pms':
      case 'pms-payments':
        return 'pms-payments';
      default:
        return null;
    }
  };

  // Get initial tab from URL or default based on user type
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    const mappedTab = getTabFromParam(tabParam);
    if (mappedTab) {
      // If admin-only tab requested by non-admin, fall back to default
      if (mappedTab === 'financials-import' && !isAdmin) {
        return 'grant-reports';
      }
      return mappedTab;
    }
    return isAdmin ? 'financials-import' : 'grant-reports';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Update tab when URL params change
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const mappedTab = getTabFromParam(tabParam);
    if (mappedTab) {
      // If admin-only tab requested by non-admin, fall back to default
      if (mappedTab === 'financials-import' && !isAdmin) {
        setActiveTab('grant-reports');
      } else {
        setActiveTab(mappedTab);
      }
    }
  }, [searchParams, isAdmin]);

  // Update URL when tab changes (optional - keeps URL in sync)
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Optionally update URL to reflect tab change
    const newParams = new URLSearchParams(searchParams);
    const tabParamMap = {
      'unconfirmed-transactions': 'unconfirmed',
      'confirmed-transactions': 'confirmed',
      'grant-reports': 'grants',
      'financials-import': 'import',
      'pms-payments': 'pms'
    };
    newParams.set('tab', tabParamMap[tab] || tab);
    setSearchParams(newParams);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'financials-import':
        return (
          <div className="w-full overflow-x-auto">
            <FinancialsImport userInfo={userInfo} key="financials-import" />
          </div>
        );
      case 'grant-reports':
        return (
          <div className="w-full overflow-x-auto">
            <GrantList userInfo={userInfo} />
          </div>
        );
      case 'unconfirmed-transactions':
        return (
          <div className="w-full overflow-x-auto">
            <BankStatement 
              month={searchParams.get('month')}
              year={searchParams.get('year')}
              defaultTab="debit"
            />
          </div>
        );
      case 'confirmed-transactions':
        return (
          <div className="w-full overflow-x-auto">
            <ConfirmedTransactions
              year={searchParams.get('year')}
              month={searchParams.get('month')}
            />
          </div>
        );
      case 'pms-payments':
        return (
          <div className="w-full overflow-x-auto">
            <PMSPayments userInfo={userInfo} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen">
      <div className="p-6">
        <div className="mb-6 max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900">Grant & Fund Management</h1>
          <p className="text-gray-600 mt-2">Manage grants, financials, and transactions</p>
        </div>

        <div className="flex gap-6 max-w-full">
          {/* Navigation Panel - Left Side */}
          <div className="w-64 flex-shrink-0 ml-6">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden sticky top-6">
            <div className="p-2 space-y-1">
              {/* Financials Import - Admin Only */}
              {isAdmin && (
                <button
                  onClick={() => handleTabChange('financials-import')}
                  className={`w-full p-3 text-left rounded-lg transition-colors ${
                    activeTab === 'financials-import'
                      ? 'bg-indigo-50 text-indigo-700 font-semibold'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        activeTab === 'financials-import'
                          ? 'bg-indigo-100'
                          : 'bg-gray-100'
                      }`}>
                        <svg className={`w-5 h-5 ${
                          activeTab === 'financials-import'
                            ? 'text-indigo-600'
                            : 'text-gray-600'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm">Financials Import</p>
                    </div>
                  </div>
                </button>
              )}

              {/* Grant Reports */}
              <button
                onClick={() => handleTabChange('grant-reports')}
                className={`w-full p-3 text-left rounded-lg transition-colors ${
                  activeTab === 'grant-reports'
                    ? 'bg-blue-50 text-blue-700 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      activeTab === 'grant-reports'
                        ? 'bg-blue-100'
                        : 'bg-gray-100'
                    }`}>
                      <svg className={`w-5 h-5 ${
                        activeTab === 'grant-reports'
                          ? 'text-blue-600'
                          : 'text-gray-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">Grant Reports</p>
                  </div>
                </div>
              </button>

              {/* Unconfirmed Transactions */}
              <button
                onClick={() => handleTabChange('unconfirmed-transactions')}
                className={`w-full p-3 text-left rounded-lg transition-colors ${
                  activeTab === 'unconfirmed-transactions'
                    ? 'bg-yellow-50 text-yellow-700 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      activeTab === 'unconfirmed-transactions'
                        ? 'bg-yellow-100'
                        : 'bg-gray-100'
                    }`}>
                      <svg className={`w-5 h-5 ${
                        activeTab === 'unconfirmed-transactions'
                          ? 'text-yellow-600'
                          : 'text-gray-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">Unconfirmed Transactions</p>
                  </div>
                </div>
              </button>

              {/* Confirmed Transactions */}
              <button
                onClick={() => handleTabChange('confirmed-transactions')}
                className={`w-full p-3 text-left rounded-lg transition-colors ${
                  activeTab === 'confirmed-transactions'
                    ? 'bg-green-50 text-green-700 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      activeTab === 'confirmed-transactions'
                        ? 'bg-green-100'
                        : 'bg-gray-100'
                    }`}>
                      <svg className={`w-5 h-5 ${
                        activeTab === 'confirmed-transactions'
                          ? 'text-green-600'
                          : 'text-gray-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">Confirmed Transactions</p>
                  </div>
                </div>
              </button>

              {/* PMS Payments */}
              <button
                onClick={() => handleTabChange('pms-payments')}
                className={`w-full p-3 text-left rounded-lg transition-colors ${
                  activeTab === 'pms-payments'
                    ? 'bg-emerald-50 text-emerald-700 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      activeTab === 'pms-payments'
                        ? 'bg-emerald-100'
                        : 'bg-gray-100'
                    }`}>
                      <svg className={`w-5 h-5 ${
                        activeTab === 'pms-payments'
                          ? 'text-emerald-600'
                          : 'text-gray-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">PMS Payments</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 mr-6">
          {renderContent()}
        </div>
      </div>
      </div>
    </div>
  );
};

export default GrantFundFinancials;
